import React, { useState } from 'react';
import { ArrowDown, ArrowUp, X, Layers } from 'lucide-react';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { FormFillInFields } from './FormFillInFields';

const norm = (s) => (s || '').toLowerCase().replace(/[\s\-_/.,]+/g, '');

export const BundleFormPicker = ({ groups, selectedIds, onChange, fieldValues, onFieldValuesChange, error }) => {
  const [search, setSearch] = useState('');
  const allForms = groups.flatMap((g) => g.forms);
  const byId = Object.fromEntries(allForms.map((f) => [f.id, f]));

  const toggle = (id) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  const move = (i, dir) => {
    const next = [...selectedIds];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange(next);
  };

  return (
    <div className="space-y-4" data-testid="bundle-form-picker">
      <div className={`border rounded-lg bg-white ${error ? 'border-red-500' : 'border-gray-300'}`}>
        <div className="p-2 border-b">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search forms to add to the bundle…"
            className="h-9 text-sm"
            data-testid="bundle-search-input"
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-2 space-y-3">
          {groups.map((g) => {
            const list = g.forms.filter((f) => norm(f.name).includes(norm(search)));
            if (!list.length) return null;
            return (
              <div key={g.heading}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-2 mb-1">{g.heading}</p>
                {list.map((f) => (
                  <label key={f.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer text-sm">
                    <Checkbox
                      checked={selectedIds.includes(f.id)}
                      onCheckedChange={() => toggle(f.id)}
                      data-testid={`bundle-option-${f.id}`}
                    />
                    {f.name}
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="space-y-3" data-testid="bundle-selected-list">
          <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <Layers className="h-4 w-4 text-amber-600" /> Signing order ({selectedIds.length} forms)
          </p>
          {selectedIds.map((id, i) => (
            <div key={id} className="border border-gray-200 rounded-lg p-3 bg-white space-y-2" data-testid={`bundle-selected-${id}`}>
              <div className="flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 text-sm font-medium">{byId[id]?.name}</span>
                <Button type="button" size="icon" variant="ghost" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`bundle-up-${id}`}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button type="button" size="icon" variant="ghost" disabled={i === selectedIds.length - 1} onClick={() => move(i, 1)} data-testid={`bundle-down-${id}`}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
                <Button type="button" size="icon" variant="ghost" onClick={() => toggle(id)} data-testid={`bundle-remove-${id}`}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <FormFillInFields
                consentId={id}
                values={fieldValues[id] || {}}
                onChange={(v) => onFieldValuesChange(id, v)}
                defaultOpen={false}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BundleFormPicker;
